import { FC } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import type { SeopSegmentTotal } from '@/types/seop';
import { fmtMarginPct, fmtMoneyBRL, fmtUnits, seopNum } from '@/utils/seopFormat';

/** A tabela está filtrada: os cartões somam só o que aparece e lembram o número do período. */
export interface RecorteDoCenario {
  /** `SPGV`, `busca "soro"` — vem de `rotuloDoRecorte`. */
  rotulo: string;
  itensDoPeriodo: number;
  totalDoPeriodo: SeopSegmentTotal;
}

interface CartoesDoCenarioProps {
  /** O total em tela: `economics.total` sem filtro, a soma das linhas visíveis com filtro. */
  total: SeopSegmentTotal;
  itens: number;
  /** Rótulo do mês selecionado, como aparece no seletor de período. */
  mes: string;
  recorte?: RecorteDoCenario;
}

/**
 * Em seis colunas (2xl) o cartão fica estreito e um valor como "R$ 11.260.343,30" vazava
 * para o cartão ao lado no tamanho padrão do KpiCard. Aqui o número encolhe com a largura
 * da tela e nunca quebra de linha; nas grades de 1 a 3 colunas vale o tamanho padrão.
 */
const Valor: FC<{ children: string }> = ({ children }) => (
  <span className="whitespace-nowrap 2xl:text-[clamp(1.25rem,1.4vw,1.875rem)]">{children}</span>
);

/** Sem recorte não há o que lembrar: o cartão já é o período inteiro. */
const PeriodoInteiro: FC<{ valor?: string }> = ({ valor }) =>
  valor === undefined ? null : <p className="text-2xs text-content-muted">período inteiro: {valor}</p>;

/** Os números do período inteiro já formatados, um por cartão; vazio quando não há recorte. */
function periodoInteiro(recorte: RecorteDoCenario | undefined): Partial<Record<'itens' | 'qtd' | 'pmv' | 'cpv' | 'margem' | 'pct', string>> {
  if (!recorte) return {};
  const t = recorte.totalDoPeriodo;
  return {
    itens: fmtUnits(recorte.itensDoPeriodo),
    qtd: fmtUnits(seopNum(t.net_qty)),
    pmv: fmtMoneyBRL(seopNum(t.final_value)),
    cpv: fmtMoneyBRL(seopNum(t.cpv_value)),
    margem: fmtMoneyBRL(seopNum(t.contribution_value)),
    pct: fmtMarginPct(seopNum(t.margin_rate)),
  };
}

/**
 * Os números do período em cartões — é o que vai para a apresentação.
 *
 * Sem filtro, saem de `economics.total`, que o motor calcula (`scenario_overall_total`).
 * Com a tabela filtrada (linha de produção ou busca), **acompanham o filtro**: quem filtra
 * SPGV quer o número do SPGV (pedido do Herrison, out/26). O número do período inteiro
 * continua à vista, em letra pequena, para ninguém levar o recorte como se fosse o total.
 * A soma do recorte usa a mesma regra do motor (`totalDasLinhas`): margem ponderada pela
 * receita.
 */
const CartoesDoCenario: FC<CartoesDoCenarioProps> = ({ total, itens, mes, recorte }) => {
  const sub = (padrao: string): string => (recorte ? recorte.rotulo : padrao);
  const subItens = recorte
    ? `${recorte.rotulo} · ${fmtUnits(itens)} de ${fmtUnits(recorte.itensDoPeriodo)}`
    : mes || 'período selecionado';
  const periodo = periodoInteiro(recorte);
  return (
    <div
      data-testid="seop-cartoes-cenario"
      className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4"
    >
      <KpiCard icon="list_alt" value={<Valor>{fmtUnits(itens)}</Valor>} label="Itens no cenário" sublabel={subItens}>
        <PeriodoInteiro valor={periodo.itens} />
      </KpiCard>
      <KpiCard icon="inventory_2" value={<Valor>{fmtUnits(seopNum(total.net_qty))}</Valor>} label="Quantidade" sublabel={sub('unidades do período')}>
        <PeriodoInteiro valor={periodo.qtd} />
      </KpiCard>
      <KpiCard icon="payments" value={<Valor>{fmtMoneyBRL(seopNum(total.final_value))}</Valor>} label="R$ PMV geral" sublabel={sub('quantidade × PMV')}>
        <PeriodoInteiro valor={periodo.pmv} />
      </KpiCard>
      <KpiCard icon="receipt_long" value={<Valor>{fmtMoneyBRL(seopNum(total.cpv_value))}</Valor>} label="CPV geral" sublabel={sub('quantidade × CPV')}>
        <PeriodoInteiro valor={periodo.cpv} />
      </KpiCard>
      <KpiCard icon="savings" value={<Valor>{fmtMoneyBRL(seopNum(total.contribution_value))}</Valor>} label="R$ margem geral" sublabel={sub('PMV − CPV')}>
        <PeriodoInteiro valor={periodo.margem} />
      </KpiCard>
      <KpiCard icon="percent" value={<Valor>{fmtMarginPct(seopNum(total.margin_rate))}</Valor>} label="% Margem" sublabel={sub('média ponderada pela receita')}>
        <PeriodoInteiro valor={periodo.pct} />
      </KpiCard>
    </div>
  );
};

export default CartoesDoCenario;
